import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { GIButton, Modal } from 'app-components';
import { ModalContextType } from '@contexts/modalContext';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { TEXTAREA_MAX_LENGTH, TEXTAREA_MIN_LENGTH } from '@constants/enrollments/enrollments.constants';
import { configs } from '@constants/configs/configs';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import { fetchDmiVerificationsData, overrideVerificationStatus } from '@actions/dmi/dmiActionCreators';

const StyledAlertWrapper = styled.div`
    .gi-alert {
        padding: 10px;
    }
`;

const StyledButton = styled.div`
    .gi-button {
        text-transform: none;
    }
`;

export const StyledTextAreaContainer = styled.div`
    .gi-textarea__label {
        display: none;
    }
    .gi-textarea__input {
        flex: 1;
        width: 100%;
        padding: 10px;
        height: 5rem !important;
        font-size: Medium;
    }
    .gi-textarea {
        display: flex;
        width: 100%;
        text-align: left;
        padding-left: 0;
        flex-direction: row;
        flex-wrap: nowrap;
        color: ${({ theme }) => theme.primaryMain};
    }
`;

interface IModalProps extends ModalContextType {
    applicantId: string;
    documentCategory: string;
    caseNumber: string;
}

export default function OverrideReasonModal({ closeModal, applicantId, documentCategory, caseNumber }: IModalProps) {
    const [overrideComment, setOverrideComment] = useState({ overrideCommentText: '' });
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const environment = useAppSelector(state => state.common.environment);
    const { language } = useAppSelector(state => state.common.environment);

    function onClose() {
        closeModal('overrideReasonModal');
    }

    function handleSubmit() {
        setOverrideComment({ overrideCommentText: '' });
        const category = documentCategory?.replace(/%20/g, '')?.replace(/\s+/g, '');
        const payload = {
            applicantID: applicantId,
            documentCategory: category,
            overrideComments: overrideComment.overrideCommentText,
            caseNumber: caseNumber,
        };
        dispatch(
            overrideVerificationStatus(configs.endpoints.DMI_OVERRIDE_DOCUMENTS, payload, data => {
                if (data === true) {
                    dispatch(
                        fetchDmiVerificationsData(
                            configs.endpoints.DMI_GET_DMI_VERIFICATIONS.processReplacements({ caseNumber, language }),
                            environment,
                        ),
                    );
                    closeModal('overrideReasonModal');
                }
            }),
        );
        onClose();
    }

    return (
        <Modal id="overrideReasonModal">
            <Modal.Header
                id="overrideReasonModalHeader"
                titleId="cap.applications.modal.overrideReason"
                onHide={onClose}
            />
            <Modal.Body id="overrideReasonModalBody">
                <Form formName="override-DMI-verification" data-testid="override-DMI-verification">
                    <StyledAlertWrapper>
                        <StyledTextAreaContainer data-testid="textarea-container">
                            <UniversalInput
                                inputData={{
                                    isInputRequired: false,
                                    type: INPUT_TYPES.TEXTAREA,
                                    name: 'overrideCommentText',
                                    value: overrideComment.overrideCommentText,
                                    liClass: ['text-left'],
                                    currentData: overrideComment,
                                    rows: 2,
                                    validationType: VALIDATION_TYPES.LENGTH,
                                    valuesToMatch: [TEXTAREA_MIN_LENGTH, TEXTAREA_MAX_LENGTH],
                                }}
                                errorClasses={['margin-l-0']}
                                inputActions={{
                                    onFieldChange: ({ overrideCommentText }) => {
                                        setOverrideComment(prev => ({ ...prev, overrideCommentText }));
                                    },
                                }}
                                data-testid="textarea-input"
                            />
                        </StyledTextAreaContainer>
                    </StyledAlertWrapper>
                </Form>
            </Modal.Body>
            <Modal.Controls>
                <GIButton id="closeButton" onClickHandler={onClose} variant="secondary" labelId="button.close" />
                <GIButton
                    id="submitButton"
                    onClickHandler={handleSubmit}
                    variant="primary"
                    labelId="button.submit"
                    isDisabled={!overrideComment.overrideCommentText}
                />
            </Modal.Controls>
        </Modal>
    );
}
